import React, { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  Box,
  Typography,
  Button,
  Paper,
  LinearProgress,
  Alert,
  Link,
} from "@mui/material";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PageContainer from "../common/PageContainer";
import PageHeader from "../common/PageHeader";

const VideoUpload = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState('');
  
  const fileInputRef = useRef(null);

  // Get API base URL
  // Handle file selection
  const handleFileSelect = (file) => {
    setError('');
    setUploadSuccess(false);
    setUploadResult(null);

    // Validate file type
    if (!file.type.startsWith('video/')) {
      setError('Please select a valid video file');
      toast.error('Please select a valid video file');
      return;
    }

    // Validate file size (5GB limit)
    const maxSize = 5 * 1024 * 1024 * 1024; // 5GB
    if (file.size > maxSize) {
      setError('File size must be less than 5GB');
      toast.error('File size must be less than 5GB');
      return;
    }

    setSelectedFile(file);
    toast.success(`Selected: ${file.name}`);
  };

  // Handle file input change
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  // Handle drag events
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  // Handle drop
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Upload video to S3
  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a video file first');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setError('');

    try {
      // Get token from sessionStorage
      const token = sessionStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      // Create FormData
      const formData = new FormData();
      formData.append('video', selectedFile);

      // Upload to backend
      const response = await axios.post(
        `/api/video/upload`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(progress);
          },
        }
      );

      if (response.data.success) {
        setUploadSuccess(true);
        setUploadResult(response.data.data);
        toast.success('Video uploaded successfully to S3!');
        console.log('Upload successful:', response.data.data);
      } else {
        throw new Error(response.data.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Upload failed';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  // Copy URL to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success('URL copied to clipboard!');
    }).catch(() => {
      toast.error('Failed to copy URL');
    });
  };

  // Reset form
  const resetForm = () => {
    setSelectedFile(null);
    setUploading(false);
    setUploadProgress(0);
    setUploadSuccess(false);
    setUploadResult(null);
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <PageContainer>
      <PageHeader
        gradient
        title="Video Upload"
        subtitle="Upload video files securely to AWS S3 storage"
        icon={<VideoLibraryIcon />}
      />

      <Paper
        elevation={0}
        sx={{
          border: "1px solid #eef0f6",
          borderRadius: "16px",
          p: 3,
          maxWidth: 720,
        }}
      >
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2.5, borderRadius: "8px", fontSize: "0.875rem" }}
            onClose={() => setError("")}
          >
            {error}
          </Alert>
        )}

        {!uploadSuccess ? (
          <>
            {/* Drag & Drop Zone */}
            <Box
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              sx={{
                border: dragActive
                  ? "2px solid #667eea"
                  : "2px dashed #CBD5E1",
                borderRadius: "16px",
                bgcolor: dragActive ? "#EBF0FF" : "#FAFBFC",
                p: 5,
                textAlign: "center",
                cursor: "pointer",
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: "#8b8fa3",
                  bgcolor: "#f5f7fb",
                },
              }}
            >
              <CloudUploadIcon
                sx={{
                  fontSize: 48,
                  color: dragActive ? "#667eea" : "#8b8fa3",
                  mb: 1.5,
                }}
              />
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "0.95rem",
                  color: "#1a1a2e",
                  mb: 0.5,
                }}
              >
                {selectedFile
                  ? "Click to change file"
                  : "Drop your video here"}
              </Typography>
              <Typography sx={{ fontSize: "0.825rem", color: "#8b8fa3", mb: 1 }}>
                or click to browse files
              </Typography>
              <Typography sx={{ fontSize: "0.75rem", color: "#8b8fa3" }}>
                Supported: MP4, AVI, MOV, WMV, FLV, WebM, MKV, 3GP -- Max
                size: 5GB
              </Typography>
            </Box>

            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="video/*"
              onChange={handleFileChange}
              style={{ display: "none" }}
            />

            {/* Selected File Info */}
            {selectedFile && (
              <Box
                sx={{
                  mt: 2.5,
                  p: 2,
                  border: "1px solid #eef0f6",
                  borderRadius: "8px",
                  bgcolor: "#f5f7fb",
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "8px",
                    bgcolor: "#eef0ff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <InsertDriveFileIcon
                    sx={{ fontSize: 22, color: "#667eea" }}
                  />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      color: "#1a1a2e",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {selectedFile.name}
                  </Typography>
                  <Typography sx={{ fontSize: "0.775rem", color: "#8b8fa3" }}>
                    {formatFileSize(selectedFile.size)} -- {selectedFile.type}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Upload Progress */}
            {uploading && (
              <Box sx={{ mt: 2.5 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 0.75,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.825rem",
                      fontWeight: 600,
                      color: "#5a5f7a",
                    }}
                  >
                    Uploading...
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.825rem",
                      fontWeight: 600,
                      color: "#667eea",
                    }}
                  >
                    {uploadProgress}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={uploadProgress}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    bgcolor: "#eef0f6",
                    "& .MuiLinearProgress-bar": {
                      bgcolor: "#667eea",
                      borderRadius: 4,
                    },
                  }}
                />
              </Box>
            )}

            {/* Upload Button */}
            <Box sx={{ mt: 3 }}>
              <Button
                variant="contained"
                onClick={handleUpload}
                disabled={!selectedFile || uploading}
                startIcon={<CloudUploadIcon sx={{ fontSize: 18 }} />}
                sx={{
                  textTransform: "none",
                  borderRadius: "8px",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  px: 3,
                  py: 1,
                  boxShadow: "none",
                  "&:hover": { background: "linear-gradient(135deg, #5a6fd8 0%, #6a3f9e 100%)", boxShadow: "none" },
                  "&:disabled": { bgcolor: "#8b8fa3", color: "#fff" },
                }}
              >
                {uploading ? "Uploading..." : "Upload to S3"}
              </Button>
            </Box>
          </>
        ) : (
          /* Upload Success State */
          <Box sx={{ textAlign: "center" }}>
            <CheckCircleIcon
              sx={{ fontSize: 56, color: "#059669", mb: 1.5 }}
            />
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "1.25rem",
                color: "#1a1a2e",
                mb: 0.5,
              }}
            >
              Upload Successful
            </Typography>
            <Typography
              sx={{ fontSize: "0.875rem", color: "#8b8fa3", mb: 3 }}
            >
              Your video has been uploaded to AWS S3 successfully.
            </Typography>

            {/* Video URL */}
            <Box
              sx={{
                p: 2,
                border: "1px solid #eef0f6",
                borderRadius: "8px",
                bgcolor: "#f5f7fb",
                textAlign: "left",
                mb: 2.5,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  color: "#5a5f7a",
                  mb: 1,
                }}
              >
                S3 Video URL
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Link
                  href={uploadResult.videoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    fontSize: "0.8rem",
                    color: "#667eea",
                    flex: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    display: "block",
                  }}
                >
                  {uploadResult.videoUrl}
                </Link>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
                  onClick={() => copyToClipboard(uploadResult.videoUrl)}
                  sx={{
                    textTransform: "none",
                    borderRadius: "6px",
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    borderColor: "#CBD5E1",
                    color: "#5a5f7a",
                    flexShrink: 0,
                    "&:hover": {
                      borderColor: "#8b8fa3",
                      bgcolor: "#f5f7fb",
                    },
                  }}
                >
                  Copy
                </Button>
              </Box>
            </Box>

            {/* Bucket Info */}
            <Box
              sx={{
                p: 2,
                border: "1px solid #eef0f6",
                borderRadius: "8px",
                bgcolor: "#f5f7fb",
                textAlign: "left",
                mb: 3,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  color: "#5a5f7a",
                  mb: 1.5,
                  pb: 1,
                  borderBottom: "2px solid #f0f0f5",
                }}
              >
                S3 Bucket Information
              </Typography>
              {[
                {
                  label: "Region",
                  value: uploadResult.s3Credentials.region,
                },
                {
                  label: "Bucket Name",
                  value: uploadResult.s3Credentials.bucketName,
                },
                {
                  label: "File Size",
                  value: formatFileSize(uploadResult.fileSize),
                },
                {
                  label: "Upload Time",
                  value: new Date(
                    uploadResult.uploadedAt
                  ).toLocaleString(),
                },
              ].map((item, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    py: 0.75,
                    borderBottom:
                      idx < 3 ? "1px solid #f0f2f8" : "none",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.8rem",
                      color: "#8b8fa3",
                      fontWeight: 500,
                    }}
                  >
                    {item.label}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.8rem",
                      color: "#1a1a2e",
                      fontWeight: 600,
                    }}
                  >
                    {item.value}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Reset Button */}
            <Button
              variant="outlined"
              onClick={resetForm}
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                fontWeight: 600,
                fontSize: "0.875rem",
                borderColor: "#667eea",
                color: "#667eea",
                px: 3,
                "&:hover": {
                  borderColor: "#5a6fd8",
                  bgcolor: "#eef0ff",
                },
              }}
            >
              Upload Another Video
            </Button>
          </Box>
        )}
      </Paper>
    </PageContainer>
  );
};

export default VideoUpload;
